import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import ApiPath from "./ConstantApi";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";

export interface BankTransfersData {
  id?: string;
  data: BankTransfersData;
}

export interface BankTransfersResponse {
  status: string;
  message: string;
  total?: number;
  totalRecords?: number;
  totalPages?: number;
  currentPage?: number;
  pageSize?: number;
  data?: any;
}

export interface BankTransfersListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: Array<{ key: string; value: string }>;
}

export interface ApproveBankTransferRequest {
  orderNumber: string;
  customerId: string;
}

export interface RejectBankTransferRequest {
  orderNumber: string;
  customerId: string;
  /** Shown to the customer in the cancellation email. */
  rejectReason?: string;
}

export const BANKTRANSFERS_TAG = "BankTransfers" as const;

export const BankTransfersService = createApi({
  reducerPath: "BankTransfers",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: [BANKTRANSFERS_TAG],
  endpoints: (builder) => ({
    // Orders authorised but never captured: goods delivered, money not taken.
    getPaymentFailures: builder.query<any, { page?: number; pageSize?: number }>({
      query: (params) => ({
        url: ApiPath.getPaymentFailures,
        method: "POST",
        body: { page: params?.page ?? 1, pageSize: params?.pageSize ?? 25 },
      }),
    }),
    // Retry the capture. Try this before anything else: an authorisation is
    // often still valid, and a successful capture settles the order outright.
    retryCapture: builder.mutation<any, { orderNumber: string }>({
      query: (body) => ({ url: ApiPath.retryCapture, method: "POST", body }),
    }),
    // Stop this system showing the codes. Does NOT disable them at the supplier.
    blockOrderPins: builder.mutation<any, { orderNumber: string; reason?: string }>({
      query: (body) => ({ url: ApiPath.blockOrderPins, method: "POST", body }),
    }),
    // Record that a person dealt with it, and what they decided.
    resolvePaymentFailure: builder.mutation<any, { orderNumber: string; outcome: string; note?: string }>({
      query: (body) => ({ url: ApiPath.resolvePaymentFailure, method: "POST", body }),
    }),
    getBankTransfersList: builder.query<
      BankTransfersResponse,
      BankTransfersListParams
    >({
      query: (params) => {
        const requestBody = {
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy || "createdAt",
          sortOrder: params.sortOrder || "desc",
          filters: params.filters || []
        };

        return {
          url: ApiPath.getBankTransfersList,
          method: "POST",
          body: requestBody,
        };
      },
      providesTags: [{ type: BANKTRANSFERS_TAG } as const],
    }),

    approveBankTransfers: builder.mutation<any, ApproveBankTransferRequest>({
      query: (reqBody) => ({
        url: `${ApiPath.approveBankTransfer}`,
        method: "POST",
        body: reqBody,
      }),
      invalidatesTags: [BANKTRANSFERS_TAG],
    }),

        rejectBankTransfers: builder.mutation<any, RejectBankTransferRequest>({
      query: (reqBody) => ({
        url: `${ApiPath.rejectbanktransfer}`,
        method: "POST",
        body: reqBody,
      }),
      invalidatesTags: [BANKTRANSFERS_TAG],
    }),


    getBankTransfersById: builder.query<BankTransfersResponse, string>({
      query: (id) => ({
        url: `${ApiPath.getBankTransfersById}/${id}`,
        method: "GET",
      }),
      providesTags: [BANKTRANSFERS_TAG],
    }),

    getByUserId: builder.query<BankTransfersResponse, string>({
      query: (id) => ({
        url: `${ApiPath.getByUserId}/${id}`,
        method: "GET",
      }),
      providesTags: [BANKTRANSFERS_TAG],
    }),

    deleteBankTransfers: builder.mutation<
      { status: string; message: string },
      string
    >({
      query: (id) => ({
        url: `${ApiPath.deleteBankTransfers}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [BANKTRANSFERS_TAG],
    }),
  }),
});

export const {
  useGetPaymentFailuresQuery,
  useRetryCaptureMutation,
  useBlockOrderPinsMutation,
  useResolvePaymentFailureMutation,
  useLazyGetBankTransfersListQuery,
  useApproveBankTransfersMutation,
  useRejectBankTransfersMutation,
  useDeleteBankTransfersMutation,
} = BankTransfersService;
